<template lang="pug">
div
  template(v-if="isRedirecting")
    v-container(style="max-width:1600px")
      v-skeleton-loader(type="article@5")
  template(v-else)
    v-container(fluid)
      //- Desktop layout
      template(v-if="!isMobileScreen")
        .main
          appointment-calendar-left-panel.right-border(:parentProviders.sync="selectedProviders" :parentDates.sync="selectedDates" @selected="onSelectedDate")
          appointment-calendar-middle-panel(ref="middle-calendar" :selectedId.sync="selectedId" :selectedProviders="selectedProviders" @dateChanged="onDateChanged")
          appointment-calendar-right-panel.left-border(:selectedId.sync="selectedId" @updated="onRefetchAppointments" @focusSelection="onFocusSelection")
      //- Mobile layout
      template(v-else)
        .main-mobile.pb-4
          appointment-calendar-middle-panel(ref="middle-calendar" :selectedId.sync="selectedId" :selectedProviders="selectedProviders" :isMobileView="true" @dateChanged="onDateChanged" @toggleLeftPanel="onToggleLeftDrawer")
        //- Left drawer (filter panel)
        v-navigation-drawer.z-50(v-model="showLeftDrawer" width="320" temporary left app :height="drawerHeight")
          .d-flex.flex-column.fill-height
            .d-flex.align-center.justify-space-between.px-4.py-4.sticky.top-0.white.z-1
              .text-lg.font-semibold.neutral700--text Filter
              v-card.round-lg(outlined link width="28" height="28" @click="showLeftDrawer = false")
                .d-flex.align-center.justify-center.fill-height
                  v-icon(color="neutral700" size="16") mdi-close
            .flex-grow-1.overflow-y-auto
              appointment-calendar-left-panel(:parentProviders.sync="selectedProviders" :parentDates.sync="selectedDates" @selected="onSelectedDateMobile")
        //- Right drawer (appointment details)
        v-navigation-drawer.z-50(v-model="showRightDrawer" width="375" temporary right app :height="drawerHeight")
          .d-flex.flex-column.fill-height
            .d-flex.align-center.justify-space-between.px-4.py-4.sticky.top-0.white.z-1
              .text-lg.font-semibold.neutral700--text Appointment Details
              v-card.round-lg(outlined link width="28" height="28" @click="showRightDrawer = false")
                .d-flex.align-center.justify-center.fill-height
                  v-icon(color="neutral700" size="16") mdi-close
            .flex-grow-1.overflow-hidden
              appointment-calendar-right-panel(:selectedId.sync="selectedId" :isMobileView="true" @updated="onRefetchAppointments" @focusSelection="onFocusSelection")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AppointmentCalendarLeftPanel from '@/components/clinic/appointment/calendar/AppointmentCalendarLeftPanel.vue'
import AppointmentCalendarMiddlePanel from '@/components/clinic/appointment/calendar/AppointmentCalendarMiddlePanel.vue'
import AppointmentCalendarRightPanel from '@/components/clinic/appointment/calendar/AppointmentCalendarRightPanel.vue'
import { DateTime } from 'luxon'

export default {
  components: {
    AppointmentCalendarLeftPanel,
    AppointmentCalendarMiddlePanel,
    AppointmentCalendarRightPanel
  },
  data() {
    return {
      isFetching: true,
      pageSize: 20,
      page: 1,
      appointments: [],
      selectedId: null,
      selectedProviders: [],
      ready: false,
      selectedDates: [],
      isRedirecting: true,
      showLeftDrawer: false,
      showRightDrawer: false
    }
  },
  created () {
    this.checkSelectedAppointmentView()
  },
  mounted () {
    this.checkSelectedAppointmentId()
    this.fetchClinic()
    this.ready = true
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      displaySize: 'ui/getDisplaySize'
    }),
    middleCalendar () {
      return this.ready ? this.$refs['middle-calendar'] : null
    },
    selectedType () {
      return this.middleCalendar?.selectedType || null
    },
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    drawerHeight () {
      switch (this.displaySize) {
        case 'xx-small':
          return '149dvh'
        case 'x-small':
          return '133dvh'
        case 'small':
          return '125dvh'
        case 'medium':
          return '111dvh'
        case 'large':
          return '100dvh'
        case 'x-large':
          return '91dvh'
        default:
          return '100dvh'
      }
    }
  },
  watch: {
    selectedId (value) {
      if (value && this.isMobileScreen) {
        this.showRightDrawer = true
      }
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    checkSelectedAppointmentId () {
      if (this.$route.query?.appointment) {
        this.selectedId = parseInt(this.$route.query.appointment)
      }
    },
    onRefetchAppointments () {
      this.middleCalendar.fetchAppointments()
    },
    onFocusSelection (datetimeString) {
      const datetime = DateTime.fromISO(datetimeString)
      const date = datetime.toFormat('yyyy-MM-dd')
      this.middleCalendar.focus = date
      this.middleCalendar.scrollToTime(datetime)
    },
    onDateChanged ({ start, end }) {
      // Fixing bug from v-calendar
      if (this.selectedType.value === 'category') {
        this.selectedDates = [start.date, start.date]
      } else {
        this.selectedDates = [start.date, end.date]
      }
    },
    onSelectedDate (selectedDates) {
      this.middleCalendar.focus = selectedDates[0]
      if (this.selectedType.value === 'week' || this.selectedType.value === 'month') {
        this.middleCalendar.selectedType = this.middleCalendar.types[this.middleCalendar.types.findIndex(type => type.value === 'day')]
      }
    },
    onSelectedDateMobile (selectedDates) {
      this.onSelectedDate(selectedDates)
      this.showLeftDrawer = false
    },
    onToggleLeftDrawer () {
      this.showLeftDrawer = !this.showLeftDrawer
    },
    async checkSelectedAppointmentView () {
      const selectedAppointmentView = localStorage.getItem('yz_hq_selected_appointment_view') || this.$store.getters['hq/getSelectedAppointmentView']
      if (selectedAppointmentView === 'list') {
        this.$router.push('/admin/hq/bookings')
        return
      }

      this.isRedirecting = false
    },
    fetchClinic () {
      this.$axios.get(`/clinics/${this.company.slug}?expand=form,company`)
        .then(resp => {
          this.$store.commit('clinic/setClinic', resp.data)
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
    },
  },
}
</script>

<style scoped>
.main {
  position: fixed;
  top: 72px;
  width: 100% !important;
  height: calc(100% - 72px) !important;
  display: grid;
  grid-template-columns: 17% 55% 28%;
}

.main-mobile {
  position: fixed;
  top: 72px;
  width: 100% !important;
  height: calc(100% - 72px - 87px) !important;
}

.right-border {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.left-border {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
</style>

<style lang="scss" scoped>
.v-navigation-drawer--is-mobile:not(.v-navigation-drawer--close),
.v-navigation-drawer--temporary:not(.v-navigation-drawer--close) {
  box-shadow: unset !important;
}

.v-navigation-drawer {
  ::v-deep .providers-wrapper .overflow-y-auto {
    padding-bottom: 12dvh;
  }
}
</style>